<!-- Outermost Main Container -->
<div class="overflow-y-auto w-full h-full pb-20 bg-slate-50/60 min-h-screen text-slate-800 custom-scrollbar">

    <!-- Header Banner Gradient -->
    <div class="relative pt-4 pb-8 px-4 rounded-b-[2rem] shadow-md text-white"
         style="background: linear-gradient(to right, var(--main-color, #ffc527), var(--soft-color, #ffd95d));">
        <!-- Top Bar: Back Button & Title -->
        <div class="flex items-center justify-between mb-4">
            <button 
                (click)="backToReport()"
                type="button"
                class="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all active:scale-95 shadow-sm text-white border border-white/20"
                aria-label="Go Back">
                <i class="fa-solid fa-arrow-left text-base"></i>
            </button>
            <h1 class="text-base font-bold tracking-wide drop-shadow-sm text-center flex-1 pr-10">
                {{ currentLang === 'TH' ? 'แจ้งปัญหาแอปพลิเคชัน' : 'Report App Issues' }}
            </h1>
        </div>

        <!-- Banner Info Text -->
        <div class="text-center px-4">
            <div class="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-2 border border-white/25 shadow-xs">
                <i class="fa-solid fa-mobile-screen-button text-2xl text-white"></i>
            </div>
            <p class="text-xs text-white/95 font-light leading-relaxed">
                {{ currentLang === 'TH' ? 'กรุณาระบุรายละเอียดข้อผิดพลาดของแอปพลิเคชัน เพื่อส่งให้ทีมพัฒนาตรวจสอบและแก้ไข' : 'Please provide details of the application issue for our development team to resolve.' }}
            </p>
        </div>
    </div>

    <!-- Form Container -->
    <div class="px-4 mt-6 max-w-md mx-auto space-y-4">
        <div class="bg-white/95 backdrop-blur-sm border border-slate-100 rounded-3xl p-5 shadow-sm space-y-4">
            
            <p class="text-xs text-slate-400 font-medium tracking-wide uppercase border-b border-slate-50 pb-2">
                {{ currentLang === 'TH' ? 'แบบฟอร์มการแจ้งเรื่อง' : 'Issue Submission Form' }}
            </p>        
            
            <!-- Category Field -->
            <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700">
                    {{ currentLang === "TH" ? "หมวดหมู่ปัญหา" : "Category" }}
                </label>
                <div class="relative w-full">
                    <button 
                        (click)="showDropdown = !showDropdown"
                        type="button" 
                        class="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 text-left text-xs font-medium flex justify-between items-center text-slate-800 shadow-2xs hover:bg-slate-100/50 transition-colors">
                        <span>{{ selectedCategory ? selectedCategory.label : (currentLang === 'TH' ? 'เลือกหมวดหมู่' : 'Select category') }}</span>
                        <i class="fa-solid fa-chevron-down text-slate-400 text-xs transition-transform" [ngClass]="{'rotate-180': showDropdown}"></i>
                    </button>
                    <ul *ngIf="showDropdown" class="absolute z-10 w-full bg-white border border-slate-100 rounded-xl mt-1.5 max-h-60 overflow-y-auto shadow-lg animate-fade-in py-1">
                        <li 
                            *ngFor="let category of categories" 
                            (click)="onSelectCategory(category)" 
                            class="px-4 py-2 text-xs hover:bg-slate-50 cursor-pointer text-slate-700 font-medium">                           
                            {{ category.label }}
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Details Field -->
            <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700" for="detail">
                    {{ currentLang === 'TH' ? 'รายละเอียดข้อขัดข้อง' : 'Issue Details' }}
                </label>
                <textarea 
                    name="detail" 
                    id="detail" 
                    [(ngModel)]="detail"
                    rows="4" 
                    placeholder="{{ currentLang === 'TH' ? 'ระบุปัญหา เช่น การเข้าสู่ระบบไม่ได้, ปุ่มสแกนกดไม่ติด หรือหน้าจอดำ...' : 'Specify details e.g., cannot login, scanner not responding, or blank screen...' }}"
                    class="w-full bg-slate-50 border border-slate-100 rounded-xl p-3 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 text-slate-800 leading-relaxed placeholder-slate-400 shadow-2xs transition-all"
                ></textarea>
            </div>

            <!-- Image Uploader Box -->
            <div class="space-y-2">
                <label class="text-xs font-bold text-slate-700">
                    {{ currentLang === 'TH' ? 'แนบรูปภาพหรือภาพถ่ายหน้าจอ' : 'Attach Photo or Screenshot' }}
                </label>
                
                <div class="relative w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 hover:bg-slate-100/50 transition-colors overflow-hidden flex flex-col items-center justify-center p-2 group cursor-pointer"
                     (click)="previewUrls.length === 0 ? triggerFileInput() : null">
                    
                    <!-- File input -->
                    <input
                        type="file"
                        accept="image/*"
                        hidden
                        #fileInput
                        (change)="handleFileChange($event)"
                    />

                    <!-- Empty Uploader State -->
                    <ng-container *ngIf="previewUrls.length === 0; else previewState">
                        <div class="text-center space-y-1.5 p-4">
                            <i class="fa-solid fa-camera text-3xl text-slate-400 group-hover:scale-110 transition-transform"></i>
                            <p class="text-xs font-semibold text-slate-600">
                                {{ currentLang === "TH" ? "คลิกเพื่อเลือกหรือถ่ายภาพ" : "Click to select or take photo" }}
                            </p>
                            <p class="text-[10px] text-slate-400">
                                รองรับไฟล์รูปภาพ PNG, JPG
                            </p>
                        </div>
                    </ng-container>

                    <!-- Preview State with Slides -->
                    <ng-template #previewState>
                        <div class="relative w-full h-full flex items-center justify-center">
                            <img
                                [src]="previewUrls[currentIndex]"
                                alt="Issue Screenshot Preview"
                                class="w-full h-full object-contain rounded-xl"
                            />

                            <!-- Navigation Controls -->
                            <button
                                *ngIf="previewUrls.length > 1"
                                type="button"
                                (click)="prevImage(); $event.stopPropagation()"
                                class="absolute left-2 bg-white/80 backdrop-blur-md p-2 rounded-full shadow-md text-slate-700 active:scale-95 transition-all">
                                <i class="fa-solid fa-chevron-left text-xs"></i>
                            </button>
                            <button
                                *ngIf="previewUrls.length > 1"
                                type="button"
                                (click)="nextImage(); $event.stopPropagation()"
                                class="absolute right-2 bg-white/80 backdrop-blur-md p-2 rounded-full shadow-md text-slate-700 active:scale-95 transition-all">
                                <i class="fa-solid fa-chevron-right text-xs"></i>
                            </button>

                            <!-- Delete Photo Overlay -->
                            <button 
                                type="button"
                                (click)="previewUrls = []; $event.stopPropagation()"
                                class="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all">
                                <i class="fa-solid fa-trash text-xs"></i>
                            </button>

                            <!-- Slide Dots Indicator -->
                            <div class="absolute bottom-2 flex space-x-1.5 justify-center w-full bg-slate-900/10 py-1 backdrop-blur-xs">
                                <span
                                    *ngFor="let url of previewUrls; let i = index"
                                    class="w-2 h-2 rounded-full transition-all"
                                    [ngClass]="i === currentIndex ? 'bg-amber-400 w-3' : 'bg-white/60'">
                                </span>
                            </div>
                        </div>
                    </ng-template>
                </div>
            </div>

            <!-- Action Submit / Back Buttons -->
            <div class="space-y-2 pt-2">
                <button 
                    (click)="sendReport()"
                    type="button" 
                    class="w-full py-3 text-slate-900 font-bold text-xs rounded-xl active:scale-[0.98] transition-all shadow-md flex items-center justify-center space-x-2"
                    style="background: linear-gradient(to right, var(--main-color, #ffc527), var(--soft-color, #ffd95d));">
                    <i class="fa-solid fa-paper-plane text-xs"></i>
                    <span>{{ currentLang === 'TH' ? 'ส่งรายงานปัญหา' : 'Submit Report' }}</span>
                </button>
                
                <button 
                    (click)="backToReport()"
                    type="button" 
                    class="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-xs rounded-xl active:scale-[0.98] transition-all flex items-center justify-center space-x-1.5">
                    <i class="fa-solid fa-arrow-left text-xs"></i>
                    <span>{{ currentLang === 'TH' ? 'ย้อนกลับ' : 'Back' }}</span>
                </button>
            </div>

        </div>
    </div>
</div>

<!-- Loading Overlay OUTSIDE outermost wrapper -->
<app-pss-loading *ngIf="isLoading"></app-pss-loading>